import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plane,
  Box,
  Camera,
  Users,
  Truck,
  Shield,
  Target,
  Clock,
  Globe,
  Award,
  ChevronRight,
  FileText,
} from "lucide-react";

const HomePage = () => {
  const services = [
    {
      icon: Plane,
      title: "Aviation Readiness",
      description: "Aircraft support, ground ops, ferry coordination, and airport-based deployment services.",
      link: "/aviation",
    },
    {
      icon: Box,
      title: "Government Procurement",
      description: "Government sourcing, packaging, rapid delivery, and mission-critical supply chain support.",
      link: "/government-contracting",
    },
    {
      icon: Users,
      title: "Tier-1 Veteran Teams",
      description: "Veteran manpower, rapid response teams, expeditionary logistics, and disaster response.",
      link: "/services",
    },
    {
      icon: Camera,
      title: "Aerial Photography",
      description: "Aerial imaging, infrastructure photography, site surveys, and government-grade drone services.",
      link: "/aerial-photography",
    },
    {
      icon: Truck,
      title: "Rapid Response",
      description: "Emergency logistics, time-sensitive transport, and airport-to-door delivery capability.",
      link: "/logistics",
    },
  ];

  const differentiators = [
    { icon: Shield, text: "Veteran-Led Leadership" },
    { icon: Plane, text: "Airport-Based Operations" },
    { icon: Clock, text: "72-Hour Mobilization" },
    { icon: Target, text: "Government-Ready Compliance" },
    { icon: Globe, text: "HUBZone Positioning" },
    { icon: Award, text: "Scalable Infrastructure" },
  ];

  const certifications = [
    "Veteran-Owned Small Business",
    "SAM.gov Registered",
    "HUBZone Aligned",
    "CAGE Code Active",
    "UEI Active",
    "Fully Insured",
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[90vh] flex items-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1768896335994-4da6e40858f5?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4 animate-fade-in-up">
              VETERAN-OWNED GOVERNMENT CONTRACTOR
            </p>
            <h1 className="font-heading text-5xl md:text-7xl text-white leading-tight mb-6 animate-fade-in-up animation-delay-100">
              VETERAN POWERED.<br />
              <span className="text-[#c5a04a]">MISSION READY.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-8 leading-relaxed animate-fade-in-up animation-delay-200">
              AMR Global Holdings LLC delivers rapid, reliable, and scalable aviation 
              and logistics support to government and commercial partners. Headquartered 
              at New Orleans Lakefront Airport.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-300">
              <Link to="/contact">
                <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="hero-cta-capability">
                  <FileText className="w-4 h-4 mr-2" />
                  Request Capability Statement
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="hero-cta-services">
                  Our Services
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        {/* Decorative corner elements */}
        <div className="absolute bottom-8 right-8 text-[#c5a04a] text-xs tracking-widest hidden md:block">
          <p className="font-mono">NEW ORLEANS LAKEFRONT AIRPORT</p>
          <p className="font-mono opacity-60">KNEW</p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white border-b border-slate-200" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">OUR MISSION</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                DELIVERING EXCELLENCE IN AVIATION & LOGISTICS
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                AMR Global Holdings LLC is a veteran-founded company built on military 
                discipline and mission execution. We specialize in rapid deployment support 
                for government and commercial operations, with 72-hour mobilization capability.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our team supports federal, state, municipal, and commercial clients with 
                aviation logistics, aerial operations, and Tier-1 veteran support teams.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#1a365d] p-8 rounded-sm">
                <p className="text-5xl font-heading text-[#c5a04a] mb-2">72</p>
                <p className="text-white text-sm tracking-wider">HOUR MOBILIZATION</p>
              </div>
              <div className="bg-slate-100 p-8 rounded-sm">
                <p className="text-5xl font-heading text-[#1a365d] mb-2">100%</p>
                <p className="text-slate-600 text-sm tracking-wider">VETERAN WORKFORCE</p>
              </div>
              <div className="bg-slate-100 p-8 rounded-sm">
                <p className="text-5xl font-heading text-[#1a365d] mb-2">5+</p>
                <p className="text-slate-600 text-sm tracking-wider">NAICS CODES</p>
              </div>
              <div className="bg-[#c5a04a] p-8 rounded-sm">
                <p className="text-5xl font-heading text-[#1a365d] mb-2">24/7</p>
                <p className="text-[#1a365d] text-sm tracking-wider">RAPID RESPONSE</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CORE CAPABILITIES</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">OUR SERVICES</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link key={index} to={service.link}>
                <Card className="service-card card-tactical h-full cursor-pointer" data-testid={`service-card-${index}`}>
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mb-6">
                      <service.icon className="w-7 h-7 text-[#c5a04a]" />
                    </div>
                    <h3 className="font-heading text-xl text-[#1a365d] mb-3">{service.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                    <div className="mt-6 flex items-center text-[#c5a04a] text-sm font-medium">
                      Learn More <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why AMR Section */}
      <section className="py-20 bg-[#1a365d]" data-testid="why-amr-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">DIFFERENTIATORS</p>
            <h2 className="font-heading text-3xl md:text-5xl text-white">WHY AMR GLOBAL</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {differentiators.map((item, index) => (
              <div
                key={index}
                className="text-center p-6 border border-slate-700 rounded-sm hover:border-[#c5a04a] transition-colors"
                data-testid={`differentiator-${index}`}
              >
                <item.icon className="w-10 h-10 text-[#c5a04a] mx-auto mb-4" />
                <p className="text-white text-xs font-heading tracking-wider">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">COMPLIANCE</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                CERTIFICATIONS & STATUS
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                AMR Global Holdings LLC maintains full government contractor compliance 
                and is ready to support federal, state, and municipal contract requirements.
              </p>
              <div className="flex flex-wrap gap-3">
                {certifications.map((cert, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-slate-100 text-[#1a365d] text-xs font-heading tracking-wider rounded-sm"
                    data-testid={`cert-badge-${index}`}
                  >
                    {cert}
                  </span>
                ))}
              </div>
              <Link to="/certifications" className="inline-block mt-8">
                <Button variant="outline" className="btn-tactical btn-outline text-xs" data-testid="view-certifications-btn">
                  View All Certifications
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600627290586-557423bbe623?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="US Marine Corps Flag"
                className="rounded-sm shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#c5a04a] p-6 rounded-sm">
                <p className="font-heading text-[#1a365d] text-2xl">VETERAN</p>
                <p className="font-heading text-[#1a365d] text-lg">OWNED</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20 relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1767491629923-009ffdc395d8?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-[#1a365d]/90"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-5xl text-white mb-6">
            READY TO PARTNER WITH US?
          </h2>
          <p className="text-slate-200 text-lg mb-8 max-w-2xl mx-auto">
            Contact AMR Global Holdings LLC for government contracting inquiries, 
            partnership opportunities, or to request our capability statement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="cta-contact-btn">
                Contact Us
              </Button>
            </Link>
            <Link to="/contact?type=partnership">
              <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="cta-partner-btn">
                Partner With Us
              </Button>
            </Link>
            <Link to="/contact?type=contracting">
              <Button variant="outline" className="btn-tactical border-2 border-[#c5a04a] text-[#c5a04a] hover:bg-[#c5a04a] hover:text-[#1a365d] text-sm" data-testid="cta-contracting-btn">
                Contracting Inquiry
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
